import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// Shared controller for every haklai calc table that wants parity with the
// "first table" (property parts) validation UX. Owns the UI-only state and
// DOM primitives that are identical across the property-parts and expenses
// tables:
//   - committed / touched gating for the overlay builder;
//   - a row-ref map + scroll-into-view + red-flash animation used by the
//     save/analyze "reveal first error" flow;
//   - a sticky-header on/off flag wired to MUI Collapse lifecycle;
//   - a lockable-content ref + effect that blurs any focused descendant when
//     the section is locked (so `inert` actually takes hold even if the
//     active element was inside the soon-to-be-inert subtree).
//
// Consumers are expected to keep the list of current row keys stable across
// renders (memoize upstream). On key-set changes this hook automatically
// prunes stale entries from `committedRows`, `touchedFieldsByRow`, and the
// `rowRefs` map so long sessions don't leak and id-reuse doesn't resurrect
// ghost state.

const FLASH_DURATION_MS = 700;
const ROW_SCROLL_TOP_GAP_PX = 12;
const ROW_SCROLL_BOTTOM_GAP_PX = 16;
const MIN_SCROLL_ANIMATION_DURATION_MS = 160;
const MAX_SCROLL_ANIMATION_DURATION_MS = 340;
const SCROLL_VISIBLE_FLASH_POLL_MS = 35;
const SCROLL_VISIBLE_FLASH_MAX_WAIT_MS = 700;

const parsePixelValue = (value, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Validation reveal scrolls rows into view from far below the fold. We offset
// the target so the global nav and sticky table header do not cover the row.
const getRowScrollOffset = (rowElement) => {
  const tableElement = rowElement?.closest("table");
  const tableWrapperElement = tableElement?.parentElement;
  const tableHeadElement = tableElement?.querySelector("thead");
  const wrapperStyles = tableWrapperElement ? window.getComputedStyle(tableWrapperElement) : null;
  const tableHeadStyles = tableHeadElement ? window.getComputedStyle(tableHeadElement) : null;
  const navHeight = parsePixelValue(wrapperStyles?.getPropertyValue("--haklai-nav-height"), 64);
  const headerHeightFallback = parsePixelValue(wrapperStyles?.getPropertyValue("--haklai-header-height"), 40);
  const stickyHeaderHeight = tableHeadStyles?.position === "sticky"
    ? (tableHeadElement?.getBoundingClientRect().height || headerHeightFallback)
    : 0;

  return navHeight + stickyHeaderHeight + ROW_SCROLL_TOP_GAP_PX;
};

const getRowTargetTop = (rowElement) => Math.max(
  0,
  rowElement.getBoundingClientRect().top + window.scrollY - getRowScrollOffset(rowElement)
);

const isRowVisibleWithinSafeViewport = (rowElement) => {
  const rect = rowElement.getBoundingClientRect();
  const safeTop = getRowScrollOffset(rowElement);
  const safeBottom = window.innerHeight - ROW_SCROLL_BOTTOM_GAP_PX;
  return rect.top >= safeTop && rect.bottom <= safeBottom;
};

const getScrollAnimationDuration = (distance) => Math.min(
  MAX_SCROLL_ANIMATION_DURATION_MS,
  Math.max(MIN_SCROLL_ANIMATION_DURATION_MS, Math.abs(distance) * 0.35)
);

const easeInOutCubic = (progress) => (
  progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - (Math.pow(-2 * progress + 2, 3) / 2)
);

// Prune entries from a map whose keys are no longer valid. Returns the same
// reference when nothing changed, which keeps referential equality downstream
// and avoids spurious re-renders of memoized rows.
const pruneByValidKeys = (prev, validKeys) => {
  const next = Object.keys(prev).reduce((acc, key) => {
    if (validKeys.has(key)) acc[key] = prev[key];
    return acc;
  }, {});
  return Object.keys(next).length === Object.keys(prev).length ? prev : next;
};

const useHaklaiTableValidationController = ({
  // `rowKeys` is the list of currently-live row identifiers. The hook uses
  // it for two things:
  //   1. `commitAllRows` iterates it to flag every row as committed.
  //   2. The prune effect drops stale entries whenever a row disappears.
  // Callers should memoize it (e.g. with `useMemo`) so the prune effect does
  // not fire on every render.
  rowKeys = [],
  locked = false,
  expanded = false,
  resetValidationOnRowKeyRemoval = false
} = {}) => {
  const [committedRows, setCommittedRows] = useState({});
  const [touchedFieldsByRow, setTouchedFieldsByRow] = useState({});
  const [flashingRowKey, setFlashingRowKey] = useState("");
  const [stickyHeaderEnabled, setStickyHeaderEnabled] = useState(expanded);
  const rowRefs = useRef({});
  const lockableContentRef = useRef(null);
  const flashTimeoutRef = useRef(0);
  const flashFrameRef = useRef(0);
  const scrollAnimationFrameRef = useRef(0);
  const scrollVisibleFlashPollTimeoutRef = useRef(0);
  const scrollVisibleFlashMaxTimeoutRef = useRef(0);
  const scrollVisibleFlashListenerRef = useRef(null);
  const previousRowKeysSignatureRef = useRef("");

  // Join the row-key array so the effect/memo below can key off a primitive
  // rather than array identity. Callers may or may not memoize the array
  // itself; this defensive step makes the hook forgiving.
  const rowKeysSignature = useMemo(
    () => (Array.isArray(rowKeys) ? rowKeys.filter(Boolean).join("|") : ""),
    [rowKeys]
  );

  const setRowRef = useCallback((key) => (el) => {
    if (el) { rowRefs.current[key] = el; return; }
    delete rowRefs.current[key];
  }, []);

  const markRowCommitted = useCallback((key) => {
    setCommittedRows((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  const touchRowField = useCallback((rowKey, fieldName) => {
    setTouchedFieldsByRow((prev) => {
      const rowFields = prev[rowKey] || {};
      if (rowFields[fieldName]) return prev;
      return { ...prev, [rowKey]: { ...rowFields, [fieldName]: true } };
    });
  }, []);

  // Flag every currently-known row as committed so the overlay reveals its
  // required-field errors (used by the parent when the user hits analyze /
  // save without having visited every row individually). The function is
  // stable across renders as long as `rowKeysSignature` is, so parents can
  // safely expose it via `useImperativeHandle`.
  const commitAllRows = useCallback(() => {
    setCommittedRows((prev) => {
      const keys = rowKeysSignature ? rowKeysSignature.split("|") : [];
      if (!keys.length) return prev;
      let didChange = false;
      const next = { ...prev };
      keys.forEach((key) => {
        if (!key) return;
        if (!next[key]) {
          next[key] = true;
          didChange = true;
        }
      });
      return didChange ? next : prev;
    });
  }, [rowKeysSignature]);

  // Prune every id-keyed piece of UI state when a row disappears. Keeping
  // stale entries is harmless for correctness (they'd just sit unused) but
  // would leak memory over long sessions and would re-appear as ghost state
  // if a future row took the same key. Collapsing into one effect avoids
  // double-rendering on each `rowKeys` transition.
  useEffect(() => {
    const validKeys = new Set(rowKeysSignature ? rowKeysSignature.split("|") : []);
    const previousKeys = previousRowKeysSignatureRef.current
      ? previousRowKeysSignatureRef.current.split("|").filter(Boolean)
      : [];
    const hasRemovedKey = previousKeys.some((key) => !validKeys.has(key));

    Object.keys(rowRefs.current).forEach((key) => {
      if (!validKeys.has(key)) delete rowRefs.current[key];
    });

    if (resetValidationOnRowKeyRemoval && hasRemovedKey) {
      setCommittedRows({});
      setTouchedFieldsByRow({});
      setFlashingRowKey("");
      previousRowKeysSignatureRef.current = rowKeysSignature;
      return;
    }

    setCommittedRows((prev) => pruneByValidKeys(prev, validKeys));
    setTouchedFieldsByRow((prev) => pruneByValidKeys(prev, validKeys));
    previousRowKeysSignatureRef.current = rowKeysSignature;
  }, [resetValidationOnRowKeyRemoval, rowKeysSignature]);

  const cancelRowFlash = useCallback(() => {
    if (flashTimeoutRef.current) {
      window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = 0;
    }
    if (flashFrameRef.current) {
      window.cancelAnimationFrame(flashFrameRef.current);
      flashFrameRef.current = 0;
    }
  }, []);

  const cancelScrollSettledFlash = useCallback(() => {
    if (scrollVisibleFlashPollTimeoutRef.current) {
      window.clearTimeout(scrollVisibleFlashPollTimeoutRef.current);
      scrollVisibleFlashPollTimeoutRef.current = 0;
    }
    if (scrollVisibleFlashMaxTimeoutRef.current) {
      window.clearTimeout(scrollVisibleFlashMaxTimeoutRef.current);
      scrollVisibleFlashMaxTimeoutRef.current = 0;
    }
    if (scrollVisibleFlashListenerRef.current) {
      window.removeEventListener("scroll", scrollVisibleFlashListenerRef.current);
      scrollVisibleFlashListenerRef.current = null;
    }
  }, []);

  const cancelActiveRowScroll = useCallback(() => {
    if (scrollAnimationFrameRef.current) {
      window.cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = 0;
    }
  }, []);

  useEffect(() => () => {
    cancelRowFlash();
    cancelActiveRowScroll();
    cancelScrollSettledFlash();
  }, [cancelActiveRowScroll, cancelRowFlash, cancelScrollSettledFlash]);

  // Lock handling: when the section locks, blur any focused descendant so
  // the `inert` attribute on the wrapper actually prevents further focus
  // (otherwise the active element stays focused visually even though events
  // stop firing). Runs three times — sync, next frame, next task — to catch
  // MUI's own post-close focus-restore hook that may re-focus into the tree.
  useLayoutEffect(() => {
    if (!locked) return undefined;
    const container = lockableContentRef.current;
    if (!container) return undefined;

    const blurFocusedDescendant = () => {
      const activeElement = document.activeElement;
      if (!activeElement || !container.contains(activeElement)) return;
      if (typeof activeElement.blur === "function") activeElement.blur();
    };

    blurFocusedDescendant();
    const rafId = window.requestAnimationFrame(blurFocusedDescendant);
    const timeoutId = window.setTimeout(blurFocusedDescendant, 0);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [locked]);

  const scrollToRow = useCallback((rowKey) => {
    const el = rowRefs.current[rowKey];
    cancelActiveRowScroll();
    if (!el) return;
    if (isRowVisibleWithinSafeViewport(el)) return;

    const startTop = window.scrollY;
    const targetTop = getRowTargetTop(el);
    const distance = targetTop - startTop;

    if (Math.abs(distance) < 1) {
      window.scrollTo({ top: targetTop });
      return;
    }

    const duration = getScrollAnimationDuration(distance);
    let startTime = 0;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const nextTop = startTop + (distance * easeInOutCubic(progress));
      window.scrollTo({ top: nextTop });

      if (progress < 1) {
        scrollAnimationFrameRef.current = window.requestAnimationFrame(step);
      } else {
        scrollAnimationFrameRef.current = 0;
      }
    };

    scrollAnimationFrameRef.current = window.requestAnimationFrame(step);
  }, [cancelActiveRowScroll]);

  const flashRow = useCallback((rowKey) => {
    cancelScrollSettledFlash();
    cancelRowFlash();
    setFlashingRowKey("");
    flashFrameRef.current = window.requestAnimationFrame(() => {
      flashFrameRef.current = 0;
      setFlashingRowKey(rowKey);
      flashTimeoutRef.current = window.setTimeout(() => {
        flashTimeoutRef.current = 0;
        setFlashingRowKey((prev) => (prev === rowKey ? "" : prev));
      }, FLASH_DURATION_MS);
    });
  }, [cancelRowFlash, cancelScrollSettledFlash]);

  const flashRowWhenVisible = useCallback((rowKey) => {
    cancelScrollSettledFlash();
    cancelRowFlash();
    setFlashingRowKey("");

    if (!rowKey) return;

    const rowElement = rowRefs.current[rowKey];
    if (rowElement && isRowVisibleWithinSafeViewport(rowElement)) {
      flashRow(rowKey);
      return;
    }

    const runFlash = () => {
      cancelScrollSettledFlash();
      flashRow(rowKey);
    };

    const scheduleVisibilityCheck = () => {
      if (scrollVisibleFlashPollTimeoutRef.current) {
        window.clearTimeout(scrollVisibleFlashPollTimeoutRef.current);
      }
      scrollVisibleFlashPollTimeoutRef.current = window.setTimeout(() => {
        scrollVisibleFlashPollTimeoutRef.current = 0;
        if (isRowVisibleWithinSafeViewport(rowElement)) {
          runFlash();
          return;
        }
        scheduleVisibilityCheck();
      }, SCROLL_VISIBLE_FLASH_POLL_MS);
    };
    const handleScroll = () => {
      if (isRowVisibleWithinSafeViewport(rowElement)) runFlash();
    };

    scrollVisibleFlashListenerRef.current = handleScroll;
    window.addEventListener("scroll", handleScroll, { passive: true });
    scheduleVisibilityCheck();
    scrollVisibleFlashMaxTimeoutRef.current = window.setTimeout(
      runFlash,
      SCROLL_VISIBLE_FLASH_MAX_WAIT_MS
    );
  }, [cancelRowFlash, cancelScrollSettledFlash, flashRow]);

  return {
    committedRows,
    touchedFieldsByRow,
    flashingRowKey,
    stickyHeaderEnabled,
    lockableContentRef,
    setStickyHeaderEnabled,
    setRowRef,
    touchRowField,
    markRowCommitted,
    commitAllRows,
    scrollToRow,
    flashRow,
    flashRowWhenVisible
  };
};

export default useHaklaiTableValidationController;
